import {
  BaseDataProvider,
  DataProviderParams,
  DataProviderResult,
} from "../base/BaseDataProvider";
import { ApolloClient } from "@apollo/client";
import { gql } from "@apollo/client";

const GET_NOTIFICATIONS = gql`
  query GetAllNotifications(
    $limit: Int
    $offset: Int
    $status: String
    $type: String
    $channel: String
  ) {
    allNotifications(
      limit: $limit
      offset: $offset
      status: $status
      type: $type
      channel: $channel
    ) {
      id
      type
      channel
      status
      title
      content
      recipient
      templateName
      errorMessage
      retryCount
      sentAt
      userId
      user {
        id
        name
        email
      }
      createdAt
      updatedAt
    }
  }
`;

const GET_NOTIFICATION = gql`
  query GetNotification($id: Int!) {
    notification(id: $id) {
      id
      type
      channel
      status
      title
      content
      recipient
      templateName
      errorMessage
      retryCount
      sentAt
      userId
      user {
        id
        name
        email
      }
      createdAt
      updatedAt
    }
  }
`;

const GET_NOTIFICATION_STATS = gql`
  query GetNotificationStats {
    adminNotificationStats
  }
`;

const GET_QUEUE_DETAILS = gql`
  query GetQueueDetails($limit: Int!) {
    queueDetails(limit: $limit)
  }
`;

const GET_QUEUE_STATS = gql`
  query GetQueueStats {
    queueStats
  }
`;

const RETRY_NOTIFICATION = gql`
  mutation RetryNotification($notificationId: Int!) {
    retryNotification(notificationId: $notificationId)
  }
`;

const CLEANUP_NOTIFICATIONS = gql`
  mutation CleanupOldNotifications($daysOld: Int!) {
    cleanupOldNotifications(daysOld: $daysOld)
  }
`;

export class NotificationsDataProvider extends BaseDataProvider {
  constructor(client: ApolloClient<any>) {
    super(client);
  }

  async getList(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "getList", params);

    try {
      const { pagination, filter } = params;
      const page = pagination?.page || 1;
      const perPage = pagination?.perPage || 25;
      const offset = (page - 1) * perPage;

      const result = await this.client.query({
        query: GET_NOTIFICATIONS,
        variables: {
          limit: perPage,
          offset,
          status: filter?.status,
          type: filter?.type,
          channel: filter?.channel,
        },
        fetchPolicy: "no-cache",
      });

      const data = result.data.allNotifications || [];
      this.logSuccess(resource, "getList", { dataLength: data.length });

      return {
        data,
        total: data.length, // Временно используем длину массива
      };
    } catch (error: any) {
      this.logError(resource, "getList", error, params);
      return { data: [], total: 0 };
    }
  }

  async getOne(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "getOne", params);

    try {
      const result = await this.client.query({
        query: GET_NOTIFICATION,
        variables: { id: parseInt(params.id as string, 10) },
        fetchPolicy: "network-only",
      });

      const data = result.data.notification;
      this.logSuccess(resource, "getOne", data);

      return { data };
    } catch (error: any) {
      this.logError(resource, "getOne", error, params);
      throw error;
    }
  }

  async create(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "create", params);

    // Создание уведомлений обычно не поддерживается через админку
    console.log("Create not implemented for notifications:", params);
    return { data: { id: Date.now(), ...params.data } };
  }

  async update(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "update", params);

    try {
      const result = await this.client.mutate({
        mutation: RETRY_NOTIFICATION,
        variables: { notificationId: parseInt(params.id as string, 10) },
      });

      const data = result.data.retryNotification;
      this.logSuccess(resource, "update", data);

      return { data };
    } catch (error: any) {
      this.logError(resource, "update", error, params);
      throw error;
    }
  }

  async delete(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "delete", params);

    // Удаление уведомлений обычно не поддерживается
    console.log("Delete not implemented for notifications:", params);
    return { data: { id: params.id } };
  }

  // Дополнительные методы для статистики
  async getNotificationStats(): Promise<any> {
    try {
      const result = await this.client.query({
        query: GET_NOTIFICATION_STATS,
        fetchPolicy: "network-only",
      });

      return JSON.parse(result.data.adminNotificationStats);
    } catch (error: any) {
      this.logError("notifications", "getNotificationStats", error, {});
      return [];
    }
  }

  async getQueueDetails(limit: number = 20): Promise<any> {
    try {
      const result = await this.client.query({
        query: GET_QUEUE_DETAILS,
        variables: { limit },
        fetchPolicy: "network-only",
      });

      if (!result.data?.queueDetails) {
        this.logError(
          "notifications",
          "getQueueDetails",
          "No queue details data received",
          { limit },
        );
        return { waiting: [], active: [], completed: [], failed: [] };
      }

      const parsed = JSON.parse(result.data.queueDetails);
      if (!parsed || typeof parsed !== "object") {
        this.logError(
          "notifications",
          "getQueueDetails",
          "Invalid queue details data format",
          { limit },
        );
        return { waiting: [], active: [], completed: [], failed: [] };
      }

      return parsed;
    } catch (error: any) {
      this.logError("notifications", "getQueueDetails", error, { limit });
      return { waiting: [], active: [], completed: [], failed: [] };
    }
  }

  async getQueueStats(): Promise<any> {
    try {
      const result = await this.client.query({
        query: GET_QUEUE_STATS,
        fetchPolicy: "network-only",
      });

      if (!result.data?.queueStats) {
        this.logError(
          "notifications",
          "getQueueStats",
          "No queue stats data received",
          {},
        );
        return { waiting: 0, active: 0, completed: 0, failed: 0, total: 0 };
      }

      const parsed = JSON.parse(result.data.queueStats);
      if (!parsed || typeof parsed !== "object") {
        this.logError(
          "notifications",
          "getQueueStats",
          "Invalid queue stats data format",
          {},
        );
        return { waiting: 0, active: 0, completed: 0, failed: 0, total: 0 };
      }

      return parsed;
    } catch (error: any) {
      this.logError("notifications", "getQueueStats", error, {});
      return { waiting: 0, active: 0, completed: 0, failed: 0, total: 0 };
    }
  }

  async cleanupOldNotifications(daysOld: number = 30): Promise<number> {
    try {
      const result = await this.client.mutate({
        mutation: CLEANUP_NOTIFICATIONS,
        variables: { daysOld },
      });

      return result.data.cleanupOldNotifications;
    } catch (error: any) {
      this.logError("notifications", "cleanupOldNotifications", error, {
        daysOld,
      });
      throw error;
    }
  }
}
